const IMAGE_TYPES = new Set(['image/png', 'image/jpeg', 'image/webp']);
const IMAGE_EXTENSIONS = /\.(png|jpe?g|webp)$/i;

export function isImageFile(file) {
  return Boolean(file && (IMAGE_TYPES.has(file.type) || IMAGE_EXTENSIONS.test(file.name || '')));
}

function numberFromText(value) {
  const normalized = String(value || '').replace(/,/g, '.').replace(/[^0-9.]/g, '');
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
}

function cleanLine(line) {
  return String(line || '')
    .replace(/[|]/g, 'I')
    .replace(/[‐‑‒–—]/g, '-')
    .replace(/\s+/g, ' ')
    .trim();
}

function scoredCandidates(lines, matcher, scorer) {
  const candidates = [];
  lines.forEach((line, index) => {
    const matches = [...line.matchAll(matcher)];
    matches.forEach((match) => {
      const candidate = scorer(match, line, index);
      if (candidate) candidates.push(candidate);
    });
  });
  return candidates.sort((a, b) => b.score - a.score);
}

function nearbyFilamentCost(lines, rowIndex) {
  const context = lines.slice(rowIndex + 1, rowIndex + 6).join(' ');
  const match = context.match(/\bcost\s*:?\s*[$â‚¬Â£]?\s*(\d+[.,]\d+)\b/i);
  return match ? numberFromText(match[1]) : null;
}

function repairPairedFilamentWeight(meters, grams, { detectedSlicer = '', cost = null, rawGrams = '' } = {}) {
  if (!Number.isFinite(meters) || meters <= 0 || !Number.isFinite(grams) || grams <= 0) return { grams, repaired: false };
  const plausibleRatio = (weight) => {
    const gramsPerMeter = weight / meters;
    return gramsPerMeter >= 0.5 && gramsPerMeter <= 15;
  };

  // Bambu's compact dark summary can lose both decimal points in the paired
  // length/weight row, leaving their ratio unchanged. When its nearby cost
  // retains a decimal, use a conservative retail filament cost range to select
  // the only plausible decimal position.
  if (detectedSlicer === 'Bambu Studio' && Number.isFinite(cost) && cost > 0 && !/[.,]/.test(rawGrams)) {
    for (const divisor of [1, 10, 100, 1000]) {
      const candidateGrams = grams / divisor;
      const costPerKilogram = cost / (candidateGrams / 1000);
      if (costPerKilogram >= 5 && costPerKilogram <= 200) {
        return { grams: candidateGrams, repaired: divisor !== 1 };
      }
    }
  }

  if (plausibleRatio(grams)) return { grams, repaired: false };

  for (const divisor of [10, 100, 1000]) {
    const repairedGrams = grams / divisor;
    if (plausibleRatio(repairedGrams)) return { grams: repairedGrams, repaired: true };
  }
  return { grams, repaired: false };
}

function extractFilament(lines, detectedSlicer = 'Unknown slicer') {
  const labelPattern = /(filament|material|weight|model|total|used|usage)/i;
  const excludedPattern = /(spool|diameter|density|nozzle|bed|volume|cost|price)/i;
  const candidates = scoredCandidates(lines, /(\d+(?:[.,]\d+)?)\s*(kg|g)\b/gi, (match, line, index) => {
    const amount = numberFromText(match[1]);
    if (amount === null) return null;
    const grams = match[2].toLowerCase() === 'kg' ? amount * 1000 : amount;
    if (grams <= 0 || grams > 100000) return null;
    const context = [lines[index - 2], lines[index - 1], line, lines[index + 1]].filter(Boolean).join(' ');
    let score = 20;
    if (labelPattern.test(line)) score += 55;
    else if (labelPattern.test(context)) score += 40;
    if (/filament|used|usage/i.test(line)) score += 30;
    if (/total\s+filament/i.test(context)) score += 45;
    else if (/model\s+filament/i.test(context)) score += 15;
    else if (/total/i.test(line)) score += 10;
    if (excludedPattern.test(line)) score -= 55;
    if (grams >= 1 && grams <= 10000) score += 8;
    return { value: Math.round(grams * 10) / 10, score, line, index };
  });

  // Orca and Bambu place filament length and weight together in their summaries.
  // Tesseract can confuse the final "g" with "q" or "9", or drop the decimal
  // in a weight such as 92.52 g. The paired meter value lets us reject a
  // physically impossible grams-per-meter ratio and restore the decimal.
  // Only allow that tolerance when a meter value and a nearby filament label make
  // the unit relationship unambiguous.
  lines.forEach((line, index) => {
    const labelContext = [lines[index - 4], lines[index - 3], lines[index - 2], lines[index - 1], line].filter(Boolean).join(' ');
    if (!/(?:total|model)\s+filament|filament\s+(?:model|used|usage|weight)/i.test(labelContext)) return;

    const row = line.match(/\b(\d+(?:[.,]\d+)?)\s*m(?:eters?)?\s+(\d+(?:[.,]\d+)?)\s*(g|grams?|q|9)\b/i);
    if (!row) return;
    const meters = numberFromText(row[1]);
    const rawGrams = numberFromText(row[2]);
    if (meters === null || rawGrams === null || rawGrams <= 0 || rawGrams > 100000) return;
    const { grams, repaired } = repairPairedFilamentWeight(meters, rawGrams, {
      detectedSlicer,
      cost: nearbyFilamentCost(lines, index),
      rawGrams: row[2],
    });

    let score = /total\s+filament/i.test(labelContext) ? 150 : /filament\s+model/i.test(labelContext) ? 130 : 105;
    if (repaired) score += 8;
    if (!/^g/i.test(row[3])) score -= 18;
    candidates.push({ value: Math.round(grams * 10) / 10, score, line, index });
  });

  candidates.sort((a, b) => b.score - a.score);
  return candidates.find((candidate) => candidate.score >= 52) || null;
}

function durationFromLine(line) {
  const dayHourMinute = line.match(/(\d+(?:[.,]\d+)?)\s*d(?:ays?)?\s*(?:(\d+(?:[.,]\d+)?)\s*h(?:ours?)?)?\s*(?:(\d+(?:[.,]\d+)?)\s*m(?:in(?:ute)?s?)?)?/i);
  if (dayHourMinute) {
    const days = numberFromText(dayHourMinute[1]) || 0;
    const hours = numberFromText(dayHourMinute[2]) || 0;
    const minutes = numberFromText(dayHourMinute[3]) || 0;
    return days * 24 * 60 + hours * 60 + minutes;
  }

  const hourMinute = line.match(/(\d+(?:[.,]\d+)?)\s*h(?:ours?)?\s*(?:(\d+(?:[.,]\d+)?)\s*m(?:in(?:ute)?s?)?)?/i);
  if (hourMinute) {
    const hours = numberFromText(hourMinute[1]) || 0;
    const minutes = numberFromText(hourMinute[2]) || 0;
    return hours * 60 + minutes;
  }

  const wordHours = line.match(/(\d+(?:[.,]\d+)?)\s*hours?\s*(?:(\d+(?:[.,]\d+)?)\s*minutes?)?/i);
  if (wordHours) return (numberFromText(wordHours[1]) || 0) * 60 + (numberFromText(wordHours[2]) || 0);

  const colon = line.match(/\b(\d{1,3}):(\d{2})(?::(\d{2}))?\b/);
  if (colon) {
    if (colon[3] !== undefined) return (Number(colon[1]) * 60) + Number(colon[2]) + (Number(colon[3]) / 60);
    return (Number(colon[1]) * 60) + Number(colon[2]);
  }

  const minutesOnly = line.match(/(\d+(?:[.,]\d+)?)\s*m(?:in(?:ute)?s?)\b/i);
  if (minutesOnly) return numberFromText(minutesOnly[1]);
  return null;
}

function extractPrintTime(lines) {
  const candidates = lines.map((line, index) => {
    const minutes = durationFromLine(line);
    if (minutes === null || minutes <= 0 || minutes > 60 * 24 * 60) return null;
    const precedingLabels = [lines[index - 2], lines[index - 1]].filter(Boolean).join(' ');
    const context = [precedingLabels, line, lines[index + 1]].filter(Boolean).join(' ');
    let score = 25;
    if (/(print|estimated|estimate|time|duration|total)/i.test(line)) score += 55;
    else if (/(print|estimated|estimate|time|duration|total)/i.test(context)) score += 38;
    if (/(remaining|elapsed|download|upload|slice time)/i.test(line)) score -= 45;
    if (/total\s+(?:print(?:ing)?\s+)?time/i.test(line)) score += 50;
    else if (/total\s+(?:print(?:ing)?\s+)?time/i.test(precedingLabels)) score += 60;
    else if (/(print time|printing time|estimated time)/i.test(line)) score += 25;
    else if (/(print time|printing time|estimated time)/i.test(precedingLabels)) score += 35;
    if (/prepare\s+time/i.test(line)) score -= 30;
    return { value: minutes, score, line, index };
  }).filter(Boolean).sort((a, b) => b.score - a.score);
  return candidates.find((candidate) => candidate.score >= 54) || null;
}

function extractQuantity(lines) {
  const candidates = scoredCandidates(lines, /\b(\d{1,4})\s*(?:pcs?|pieces?|copies?|objects?|instances?|parts?)\b/gi, (match, line, index) => {
    const value = Number(match[1]);
    if (!Number.isInteger(value) || value < 1 || value > 1000) return null;
    let score = 45;
    if (/(quantity|count|copies|objects|instances|parts|pieces)/i.test(line)) score += 35;
    return { value, score, line, index };
  });

  lines.forEach((line, index) => {
    const match = line.match(/(?:quantity|copies|objects|instances|parts|pieces)\s*[:x-]?\s*(\d{1,4})/i);
    if (!match) return;
    const value = Number(match[1]);
    if (value >= 1 && value <= 1000) candidates.push({ value, score: 90, line, index });
  });

  return candidates.sort((a, b) => b.score - a.score)[0] || null;
}

function detectMaterial(text) {
  const materials = [
    ['PLA', /\bPLA(?:\+| PLUS| PRO| MATTE| BASIC| SILK| TOUGH)?\b/i],
    ['PETG', /\bPETG(?:-CF| CF| HF)?\b/i],
    ['ABS', /\bABS(?:-GF| GF)?\b/i],
    ['ASA', /\bASA(?:-CF| CF)?\b/i],
    ['TPU', /\bTPU(?:\s*\d{2,3}A)?\b/i],
    ['PC', /\bPOLYCARBONATE\b|\bPC(?:-CF| CF)?\b/i],
    ['PA', /\bNYLON\b|\bPA(?:6|12|HT)?(?:-CF| CF|-GF| GF)?\b/i],
    ['PVA', /\bPVA\b/i],
    ['HIPS', /\bHIPS\b/i],
    ['RESIN', /\bRESIN\b/i],
  ];
  return materials.find(([, pattern]) => pattern.test(text))?.[0] || '';
}

function detectSlicer(text) {
  const slicers = [
    ['Bambu Studio', /bambu[\s_-]*studio|bambu[\s_-]*lab/i],
    ['OrcaSlicer', /orca[\s_-]*slicer/i],
    ['PrusaSlicer', /prusaslicer|prusa slicer/i],
    ['Cura', /ultimaker cura|\bcura\b/i],
    ['Creality Print', /creality print/i],
    ['Lychee Slicer', /lychee slicer/i],
    ['Chitubox', /chitubox/i],
  ];
  return slicers.find(([, pattern]) => pattern.test(text))?.[0] || 'Unknown slicer';
}

function confidenceFor(candidate, ocrConfidence) {
  if (!candidate) return 0;
  const labelConfidence = Math.max(0, Math.min(100, candidate.score));
  return Math.round((labelConfidence * 0.65) + (Math.max(0, ocrConfidence) * 0.35));
}

export function parseSlicerText(rawText, ocrConfidence = 0, sourceName = '') {
  const text = String(rawText || '').split('\u0000').join(' ');
  const lines = text.split(/\r?\n/).map(cleanLine).filter(Boolean);
  const detectedSlicer = detectSlicer(`${sourceName}\n${text}`);
  const filament = extractFilament(lines, detectedSlicer);
  const printTime = extractPrintTime(lines);
  const quantity = extractQuantity(lines);
  const fields = {};
  const fieldConfidence = {};

  if (filament) {
    fields.filamentGrams = filament.value;
    fieldConfidence.filamentGrams = confidenceFor(filament, ocrConfidence);
  }
  if (printTime) {
    const totalMinutes = Math.round(printTime.value);
    fields.printHours = Math.floor(totalMinutes / 60);
    fields.printMinutes = totalMinutes % 60;
    fieldConfidence.printTime = confidenceFor(printTime, ocrConfidence);
  }
  if (quantity) {
    fields.quantity = quantity.value;
    fieldConfidence.quantity = confidenceFor(quantity, ocrConfidence);
  }

  const confidenceValues = Object.values(fieldConfidence);
  const extractionConfidence = confidenceValues.length
    ? Math.round(confidenceValues.reduce((sum, value) => sum + value, 0) / confidenceValues.length)
    : Math.round(Math.max(0, ocrConfidence) * 0.5);

  return {
    fields,
    fieldConfidence,
    extractionConfidence,
    detectedMaterial: detectMaterial(text),
    detectedSlicer,
    rawText: text.trim().slice(0, 12000),
    lines,
  };
}

async function loadDrawable(file) {
  if ('createImageBitmap' in window) {
    try {
      return await createImageBitmap(file);
    } catch {
      // Safari fallbacks to an HTMLImageElement below.
    }
  }

  const objectUrl = URL.createObjectURL(file);
  try {
    const image = new Image();
    image.decoding = 'async';
    image.src = objectUrl;
    await image.decode();
    return image;
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

function makeProcessedCanvases(drawable) {
  const sourceWidth = drawable.width || drawable.naturalWidth;
  const sourceHeight = drawable.height || drawable.naturalHeight;
  const maxDimension = 2400;
  const targetScale = Math.min(2.25, Math.max(1, 2200 / Math.max(sourceWidth, sourceHeight)));
  const width = Math.max(1, Math.round(Math.min(sourceWidth * targetScale, maxDimension)));
  const height = Math.max(1, Math.round(width * (sourceHeight / sourceWidth)));
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d', { willReadFrequently: true });
  if (!context) throw new Error('This browser could not prepare the image for OCR.');
  context.imageSmoothingEnabled = true;
  context.imageSmoothingQuality = 'high';
  context.drawImage(drawable, 0, 0, width, height);

  const imageData = context.getImageData(0, 0, width, height);
  const pixels = imageData.data;
  let luminanceTotal = 0;
  for (let index = 0; index < pixels.length; index += 4) {
    luminanceTotal += (pixels[index] * 0.2126) + (pixels[index + 1] * 0.7152) + (pixels[index + 2] * 0.0722);
  }
  const averageLuminance = luminanceTotal / (pixels.length / 4);
  const shouldInvert = averageLuminance < 118;

  for (let index = 0; index < pixels.length; index += 4) {
    let gray = (pixels[index] * 0.2126) + (pixels[index + 1] * 0.7152) + (pixels[index + 2] * 0.0722);
    if (shouldInvert) gray = 255 - gray;
    gray = Math.max(0, Math.min(255, ((gray - 128) * 1.65) + 128));
    pixels[index] = gray;
    pixels[index + 1] = gray;
    pixels[index + 2] = gray;
    pixels[index + 3] = 255;
  }
  context.putImageData(imageData, 0, 0);

  const alternate = document.createElement('canvas');
  alternate.width = width;
  alternate.height = height;
  const alternateContext = alternate.getContext('2d', { willReadFrequently: true });
  if (!alternateContext) throw new Error('This browser could not prepare the alternate OCR pass.');
  alternateContext.drawImage(canvas, 0, 0);
  const alternateData = alternateContext.getImageData(0, 0, width, height);
  for (let index = 0; index < alternateData.data.length; index += 4) {
    const value = alternateData.data[index] > 168 ? 255 : alternateData.data[index] < 82 ? 0 : alternateData.data[index];
    alternateData.data[index] = value;
    alternateData.data[index + 1] = value;
    alternateData.data[index + 2] = value;
  }
  alternateContext.putImageData(alternateData, 0, 0);

  return { primary: canvas, alternate, shouldInvert, width, height };
}

function betterResult(first, second) {
  const firstFields = Object.keys(first.fields).length;
  const secondFields = Object.keys(second.fields).length;
  if (secondFields > firstFields) return second;
  if (secondFields === firstFields && second.extractionConfidence > first.extractionConfidence) return second;
  return first;
}

export async function recognizeSlicerScreenshot(file, onProgress = () => {}) {
  if (!isImageFile(file)) throw new Error('OCR can only analyze PNG, JPG, JPEG, or WebP images.');

  onProgress({ stage: 'prepare', progress: 0.04, label: 'Preparing image' });
  const drawable = await loadDrawable(file);
  const canvases = makeProcessedCanvases(drawable);
  drawable.close?.();

  const { createWorker, PSM } = await import('tesseract.js');
  let pass = 1;
  const worker = await createWorker('eng', 1, {
    workerPath: 'https://cdn.jsdelivr.net/npm/tesseract.js@7/dist/worker.min.js',
    corePath: 'https://cdn.jsdelivr.net/npm/tesseract.js-core@7',
    langPath: 'https://tessdata.projectnaptha.com/4.0.0',
    logger: (message) => {
      if (message.status !== 'recognizing text') return;
      const passOffset = pass === 1 ? 0.12 : 0.58;
      const passScale = pass === 1 ? 0.42 : 0.32;
      onProgress({
        stage: 'recognize',
        progress: Math.min(0.94, passOffset + (Number(message.progress || 0) * passScale)),
        label: pass === 1 ? 'Reading slicer summary' : 'Checking a second image pass',
      });
    },
  });

  try {
    await worker.setParameters({
      tessedit_pageseg_mode: PSM.SPARSE_TEXT,
      preserve_interword_spaces: '1',
    });
    const firstRecognition = await worker.recognize(canvases.primary);
    const first = parseSlicerText(firstRecognition.data.text, firstRecognition.data.confidence, file.name);
    let best = { ...first, ocrConfidence: Math.round(firstRecognition.data.confidence || 0), passes: 1 };

    // Hours and minutes are two form fields but only one logical OCR result. Run
    // the alternate pass whenever either core slicer total is still missing.
    const shouldRunSecondPass = first.fields.filamentGrams === undefined
      || first.fields.printHours === undefined
      || first.extractionConfidence < 64;
    if (shouldRunSecondPass) {
      pass = 2;
      await worker.setParameters({ tessedit_pageseg_mode: PSM.AUTO });
      const secondRecognition = await worker.recognize(canvases.alternate);
      const combinedText = `${firstRecognition.data.text}\n${secondRecognition.data.text}`;
      const combinedConfidence = Math.max(firstRecognition.data.confidence || 0, secondRecognition.data.confidence || 0);
      const second = parseSlicerText(combinedText, combinedConfidence, file.name);
      best = {
        ...betterResult(first, second),
        ocrConfidence: Math.round(combinedConfidence),
        passes: 2,
      };
    }

    onProgress({ stage: 'complete', progress: 1, label: 'Import ready for review' });
    return {
      ...best,
      imageWidth: canvases.width,
      imageHeight: canvases.height,
      darkModeImage: canvases.shouldInvert,
    };
  } finally {
    await worker.terminate();
  }
}
